import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { t } from '@/i18n';

const hours = Array.from({ length: 17 }, (_, i) => `${(6 + i).toString().padStart(2, '0')}:00`);

const MATERIAL_I18N = {
  'Limestone GCC': 'planning.material.gcc',
  'Limestone PCC': 'planning.material.pcc',
  'By-product': 'planning.material.byproduct',
};

const MATERIAL_SHORT = {
  'Limestone GCC': 'GCC',
  'Limestone PCC': 'PCC',
  'By-product': 'Vedl.',
};

const MAIN_MATERIALS = ['Limestone GCC', 'Limestone PCC'];
const BYPRODUCT_MATERIALS = ['By-product'];

export const GanttView = ({ planningData, materialColors, onSlotClick }) => {
  if (!planningData?.lines) return null;

  const now = new Date();
  const nowHour = now.getHours() + now.getMinutes() / 60;
  const nowInRange = nowHour >= 6 && nowHour <= 22;
  const nowLabel = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* Time axis */}
        <div className="flex ml-24">
          {hours.filter((_, i) => i % 2 === 0).map(h => (
            <div key={h} className="flex-1 text-[11px] text-muted-foreground font-medium">{h}</div>
          ))}
        </div>

        {/* Lines */}
        {planningData.lines.map(line => (
          <div key={line.id} className="flex items-center gap-3">
            <div className="w-20 text-sm font-semibold shrink-0">{t(line.name)}</div>
            <div className="flex-1 relative h-12 bg-muted/40 rounded-lg overflow-hidden">
              {/* NOW marker */}
              {nowInRange && (
                <div className="absolute top-0 bottom-0 z-10 flex flex-col items-center" style={{ left: `${((nowHour - 6) / 16) * 100}%` }}>
                  <div className="w-0.5 h-full bg-red-500" />
                </div>
              )}

              {/* Schedule bars */}
              {line.schedule.filter(s => s.material).map((slot, i) => {
                const totalActive = line.schedule.filter(s => s.material).length;
                const blockWidth = Math.min(90 / totalActive, 25);
                const blockLeft = 5 + i * (90 / totalActive);
                const color = materialColors[slot.material] || '#94a3b8';

                return (
                  <Tooltip key={`${line.id}-${slot.day}`}>
                    <TooltipTrigger asChild>
                      <div
                        className="absolute top-1.5 bottom-1.5 rounded-md cursor-pointer hover:brightness-110 transition-all flex items-center px-2"
                        style={{
                          left: `${blockLeft}%`,
                          width: `${blockWidth}%`,
                          backgroundColor: color,
                        }}
                        onClick={() => onSlotClick?.(slot, t(line.name), line.id)}
                      >
                        <span className="text-[10px] font-bold text-white truncate">{MATERIAL_SHORT[slot.material] || slot.grade?.split(' ')[0] || slot.material}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <div className="text-xs space-y-0.5">
                        <div className="font-bold">{MATERIAL_I18N[slot.material] ? t(MATERIAL_I18N[slot.material]) : slot.material} — {slot.grade}</div>
                        <div>{t(`mock.day.${slot.day}`)} · {slot.utilization}% {t('gantt.util')} · {slot.plannedOutput}t {t('gantt.planned')}</div>
                        {slot.orderId && <div className="text-muted-foreground">{slot.orderId}</div>}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        ))}

        {/* Legend – seskupená: Hlavní výroba | Vedlejší produkt */}
        <div className="flex flex-wrap items-center gap-4 mt-2 ml-24">
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">{t('planning.mainProduction')}</span>
            {MAIN_MATERIALS.filter(m => materialColors[m]).map(mat => (
              <div key={mat} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: materialColors[mat] }} />
                <span>{MATERIAL_I18N[mat] ? t(MATERIAL_I18N[mat]) : mat}</span>
              </div>
            ))}
          </div>
          <div className="w-px h-4 bg-border" />
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">{t('planning.byProduct')}</span>
            {BYPRODUCT_MATERIALS.filter(m => materialColors[m]).map(mat => (
              <div key={mat} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: materialColors[mat] }} />
                <span>{MATERIAL_I18N[mat] ? t(MATERIAL_I18N[mat]) : mat}</span>
              </div>
            ))}
          </div>
          {nowInRange && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground ml-4">
              <div className="w-4 h-0.5 bg-red-500 rounded" />
              <span>{t('gantt.now')} ({nowLabel})</span>
            </div>
          )}
        </div>

        {/* Kontextový text o výrobním toku */}
        <div className="mt-3 ml-24 p-3 rounded-lg bg-muted/40 text-xs text-muted-foreground max-w-2xl">
          <span className="font-semibold text-foreground">{t('planning.productionFlow')}:</span> {t('planning.productionFlowDesc')}
        </div>
      </div>
    </TooltipProvider>
  );
};
